// src/components/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Twitter, Facebook, Mail, Phone, MapPin, Heart } from 'lucide-react';

export default function Footer() {
  return (
    <footer className="bg-gray-900 text-white">
      {/* Top wave */}
      <div className="h-16 bg-cream" style={{ clipPath: 'ellipse(100% 100% at 50% 0%)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">♡</span>
              </div>
              <span className="font-display font-bold text-xl">
                Couple<span className="text-rose-400">Cart</span>
              </span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed font-body">
              Crafting love stories through thoughtful gifts. Every gift tells a story — let us help you tell yours.
            </p>
            <div className="flex gap-3 mt-5">
              {[
                { Icon: Instagram, href: '#' },
                { Icon: Twitter, href: '#' },
                { Icon: Facebook, href: '#' },
              ].map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-rose-500 flex items-center justify-center transition-all"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display font-semibold text-base mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {[
                { to: '/', label: 'Home' },
                { to: '/products', label: 'Shop All' },
                { to: '/products?category=personalized', label: 'Personalized Gifts' },
                { to: '/products?category=surprise', label: 'Surprise Boxes' },
                { to: '/products?category=handmade', label: 'Handmade' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-400 hover:text-rose-400 text-sm transition-colors font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-display font-semibold text-base mb-4">Support</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Track Order', to: '/orders' },
                { label: 'Shipping Policy', to: '/policy' },
                { label: 'Return Policy', to: '/policy' },
                { label: 'Privacy Policy', to: '/policy' },
                { label: 'FAQs', to: '/policy' },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-gray-400 hover:text-rose-400 text-sm transition-colors font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-semibold text-base mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Mail size={16} className="text-rose-400 shrink-0" />
                <span className="font-body">hello@couplecart.in</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Phone size={16} className="text-rose-400 shrink-0" />
                <span className="font-body">+91 98765 43210</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin size={16} className="text-rose-400 shrink-0 mt-0.5" />
                <span className="font-body">Bangalore, Karnataka, India</span>
              </li>
            </ul>
            {/* Payment Icons */}
            <div className="mt-5">
              <p className="text-xs text-gray-500 mb-2 font-body">Secure Payments via</p>
              <div className="flex items-center gap-2">
                <div className="bg-white/10 rounded px-2 py-1 text-xs font-bold">Razorpay</div>
                <div className="bg-white/10 rounded px-2 py-1 text-xs font-bold">UPI</div>
                <div className="bg-white/10 rounded px-2 py-1 text-xs font-bold">Cards</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-sm font-body flex items-center gap-1">
            Made with <Heart size={14} className="text-rose-400 fill-rose-400" /> by CoupleCart © {new Date().getFullYear()}
          </p>
          <p className="text-gray-600 text-xs font-body">All rights reserved. Spread love, not just gifts.</p>
        </div>
      </div>
    </footer>
  );
}
